import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, Users, Briefcase, Handshake, Calendar, GraduationCap,
  ArrowRight, CheckCircle, Send, Upload, Globe, Shield, FileText,
  User, MapPin, Clock, Plane
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Programme areas for volunteer preference
const programmeAreas = [
  'Humanitarian Aid & Emergency Response',
  'Climate Action & Environment',
  'Education & Lifelong Learning',
  'Youth Empowerment & Employability',
  'Gender Equality & Social Inclusion',
  'Health & Well-Being',
  'Migration & Social Cohesion',
  'Communications & Social Media',
  'Fundraising & Partnerships',
  'Research, Monitoring & Evaluation (MEL)',
  'Administration & Coordination'
];

// Skills options
const skillOptions = [
  'Project support',
  'Community engagement',
  'Teaching/Training',
  'Translation/Interpretation',
  'Graphic design',
  'Video editing',
  'Website/WordPress',
  'Data analysis',
  'Writing/Reporting',
  'Fundraising',
  'Event planning',
  'Logistics/Distribution'
];

const opportunities = [
  {
    icon: Users,
    title: 'Volunteer Globally',
    description: 'Join our international volunteer program and make a direct impact in communities.',
    color: '#7BC4E2'
  },
  {
    icon: GraduationCap,
    title: 'Internship Program',
    description: 'Gain valuable experience in international development and humanitarian work.',
    color: '#E08B7A'
  },
  {
    icon: Briefcase,
    title: 'Career Opportunities',
    description: 'Join our professional team and build a meaningful career.',
    color: '#9DB9A3'
  },
  {
    icon: Handshake,
    title: 'Partnerships',
    description: 'Collaborate with OPAD through strategic partnerships and MoUs.',
    color: '#E5B75F'
  }
];

export default function GetInvolvedPage() {
  const fileInputRef = useRef(null);
  const [currentSection, setCurrentSection] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  
  const [formData, setFormData] = useState({
    // Section A - Personal Details
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    country_of_residence: '',
    city: '',
    languages_spoken: '',
    
    // Section B - Volunteer Preference
    volunteer_type: '',
    programme_areas: [],
    preferred_roles: '',
    hours_per_week: '',
    start_date: '',
    duration: '',
    time_zone: '',
    
    // Section C - Skills & Experience
    current_occupation: '',
    relevant_skills: [],
    other_skills: '',
    previous_experience: '',
    motivation_statement: '',
    portfolio_linkedin: '',
    
    // Section D - Location & Travel
    able_to_travel: '',
    has_passport: '',
    visa_restrictions: '',
    preferred_regions: '',
    
    // Section E - Safeguarding
    criminal_conviction: 'no',
    criminal_explanation: '',
    agrees_to_code_of_conduct: false,
    reference1_name: '',
    reference1_organization: '',
    reference1_email: '',
    reference1_phone: '',
    reference2_name: '',
    reference2_organization: '',
    reference2_email: '',
    reference2_phone: '',
    
    // Section F - Consent
    consent_data_processing: false,
    consent_newsletter: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or Word document');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setCvFile(file);
      toast.success('CV uploaded successfully');
    }
  };

  const validateSection = (section) => {
    switch (section) {
      case 1:
        if (!formData.first_name || !formData.last_name || !formData.email || 
            !formData.phone || !formData.date_of_birth || !formData.nationality || 
            !formData.country_of_residence || !formData.city || !formData.languages_spoken) {
          toast.error('Please fill in all required fields in Personal Details');
          return false;
        }
        return true;
      case 2:
        if (!formData.volunteer_type || formData.programme_areas.length === 0 || 
            !formData.hours_per_week || !formData.start_date || !formData.duration || !formData.time_zone) {
          toast.error('Please fill in all required fields in Volunteer Preference');
          return false;
        }
        return true;
      case 3:
        if (!formData.current_occupation || formData.relevant_skills.length === 0 || 
            !formData.motivation_statement || !cvFile) {
          toast.error('Please fill in all required fields in Skills & Experience (including CV upload)');
          return false;
        }
        return true;
      case 4:
        if (!formData.able_to_travel) {
          toast.error('Please indicate if you are able to travel');
          return false;
        }
        return true;
      case 5:
        if (!formData.agrees_to_code_of_conduct || !formData.reference1_name || 
            !formData.reference1_email || !formData.reference2_name || !formData.reference2_email) {
          toast.error('Please complete all required fields in Safeguarding & Compliance');
          return false;
        }
        return true;
      case 6:
        if (!formData.consent_data_processing) {
          toast.error('Please consent to data processing to submit your application');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextSection = () => {
    if (validateSection(currentSection)) {
      setCurrentSection(prev => Math.min(prev + 1, 6));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevSection = () => {
    setCurrentSection(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSection(6)) return;
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (Array.isArray(formData[key])) {
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add CV file
      if (cvFile) {
        submitData.append('cv_file', cvFile);
      }
      
      await axios.post(`${API}/volunteers`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Thank you for applying to volunteer with OPAD! Our team will review your application and contact you if shortlisted.');
      
      // Reset form
      setFormData({
        first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
        gender: '', nationality: '', country_of_residence: '', city: '', languages_spoken: '',
        volunteer_type: '', programme_areas: [], preferred_roles: '', hours_per_week: '',
        start_date: '', duration: '', time_zone: '', current_occupation: '',
        relevant_skills: [], other_skills: '', previous_experience: '', motivation_statement: '',
        portfolio_linkedin: '', able_to_travel: '', has_passport: '', visa_restrictions: '',
        preferred_regions: '', criminal_conviction: 'no', criminal_explanation: '',
        agrees_to_code_of_conduct: false, reference1_name: '', reference1_organization: '',
        reference1_email: '', reference1_phone: '', reference2_name: '', reference2_organization: '',
        reference2_email: '', reference2_phone: '', consent_data_processing: false, consent_newsletter: false
      });
      setCvFile(null);
      setCurrentSection(1);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit application. Please try again.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sectionTitles = [
    { num: 1, title: 'Personal Details', icon: User },
    { num: 2, title: 'Volunteer Preference', icon: Heart },
    { num: 3, title: 'Skills & Experience', icon: Briefcase },
    { num: 4, title: 'Location & Travel', icon: Plane },
    { num: 5, title: 'Safeguarding', icon: Shield },
    { num: 6, title: 'Consent', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-[#FDF8F4]">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/20 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#4A7C59]/30 rounded-tr-[180px]" />
          <div className="absolute top-[30%] left-[10%] w-[150px] h-[150px] bg-[#E5B75F]/20 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Heart className="w-16 h-16 text-white mx-auto mb-6" />
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                Get Involved with <span className="text-[#E5B75F]">OPAD</span>
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                Join our global community of changemakers. Whether you volunteer, intern, or partner with us, 
                your contribution helps empower communities worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Opportunities Grid */}
      <section className="relative section-padding overflow-hidden" data-testid="opportunities-section">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 left-0 w-[300px] h-[250px] bg-[#7BC4E2]/10 rounded-br-[150px]" />
          <div className="absolute bottom-0 right-0 w-[250px] h-[200px] bg-[#E08B7A]/10 rounded-tl-[100px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Ways to Get Involved
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose the path that best fits your skills, availability, and passion for making a difference.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {opportunities.map((opp, index) => (
              <motion.div
                key={opp.title}
                className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 text-center hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`opportunity-${index}`}
              >
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${opp.color}20` }}
                >
                  <opp.icon className="w-7 h-7" style={{ color: opp.color }} />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{opp.title}</h3>
                <p className="text-sm text-slate-600">{opp.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Youth Programme Image Banner */}
      <section className="relative overflow-hidden py-8" data-testid="youth-image-banner">
        <div className="container-custom">
          <motion.div
            className="relative max-w-5xl mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/af0awud4_WhatsApp%20Image%202026-02-19%20at%2019.46.30.jpeg"
              alt="OPAD Youth Programme - Empowering Young Leaders"
              className="w-full h-auto object-cover rounded-2xl shadow-xl"
              data-testid="youth-programme-image"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl p-6">
              <p className="text-center text-white font-bold text-xl">
                Youth Empowerment in Action
              </p>
              <p className="text-center text-white/80 text-sm mt-1">
                Join our vibrant community of young changemakers making a difference worldwide
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Volunteer Application Form */}
      <section className="relative section-padding overflow-hidden" id="volunteer" data-testid="volunteer-section">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#4A7C59]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
                Application for OPAD Internship Program
              </h2>
              <p className="text-slate-600">
                Complete all sections below to apply for our internship program.
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                {sectionTitles.map((section) => (
                  <div 
                    key={section.num}
                    className={`flex flex-col items-center cursor-pointer transition-all ${
                      currentSection === section.num ? 'scale-110' : ''
                    }`}
                    onClick={() => setCurrentSection(section.num)}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-colors ${
                      currentSection >= section.num 
                        ? 'bg-[#4A7C59] text-white' 
                        : 'bg-slate-200 text-slate-500'
                    }`}>
                      <section.icon className="w-5 h-5" />
                    </div>
                    <span className={`text-xs font-medium hidden md:block ${
                      currentSection >= section.num ? 'text-[#4A7C59]' : 'text-slate-400'
                    }`}>
                      {section.title}
                    </span>
                  </div>
                ))}
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#4A7C59] transition-all duration-300"
                  style={{ width: `${(currentSection / 6) * 100}%` }}
                />
              </div>
            </div>

            <motion.div
              className="bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={currentSection}
            >
              <form onSubmit={handleSubmit} data-testid="volunteer-form">
                
                {/* Section A - Personal Details */}
                {currentSection === 1 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                      <User className="w-6 h-6 text-[#7BC4E2]" />
                      <h3 className="text-xl font-bold text-slate-900">Section A — Personal Details</h3>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-first-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-last-name"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone/WhatsApp *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-phone"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="date_of_birth">Date of Birth *</Label>
                        <Input
                          id="date_of_birth"
                          name="date_of_birth"
                          type="date"
                          value={formData.date_of_birth}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-dob"
                        />
                      </div>
                      <div>
                        <Label htmlFor="gender">Gender (Optional)</Label>
                        <Select 
                          value={formData.gender}
                          onValueChange={(value) => handleSelectChange('gender', value)}
                        >
                          <SelectTrigger className="mt-1" data-testid="volunteer-gender">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="non-binary">Non-binary</SelectItem>
                            <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="nationality">Nationality *</Label>
                        <Input
                          id="nationality"
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-nationality"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country_of_residence">Country of Residence *</Label>
                        <Input
                          id="country_of_residence"
                          name="country_of_residence"
                          value={formData.country_of_residence}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-country"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                          className="mt-1"
                          data-testid="volunteer-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="languages_spoken">Languages Spoken *</Label>
                        <Input
                          id="languages_spoken"
                          name="languages_spoken"
                          value={formData.languages_spoken}
                          onChange={handleChange}
                          required
                          placeholder="e.g., English, Swedish, French"
                          className="mt-1"
                          data-testid="volunteer-languages"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Section B - Volunteer Preference */}
                {currentSection === 2 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                      <Heart className="w-6 h-6 text-[#E08B7A]" />
                      <h3 className="text-xl font-bold text-slate-900">Section B — Volunteer Preference</h3>
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Volunteer Type * (Choose one)</Label>
                      <RadioGroup 
                        value={formData.volunteer_type}
                        onValueChange={(value) => handleSelectChange('volunteer_type', value)}
                        className="mt-3 space-y-3"
                      >
                        {[
                          { value: 'remote', label: 'Remote (Online)' },
                          { value: 'in-country', label: 'In-country (Local volunteering)' },
                          { value: 'international', label: 'International (Travel-based)' },
                          { value: 'hybrid', label: 'Hybrid (Remote + occasional onsite)' }
                        ].map((option) => (
                          <div key={option.value} className="flex items-center space-x-3">
                            <RadioGroupItem value={option.value} id={option.value} />
                            <Label htmlFor={option.value} className="font-normal cursor-pointer">
                              {option.label}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Preferred Programme Area(s) * (Tick all that apply)</Label>
                      <div className="grid md:grid-cols-2 gap-3 mt-3">
                        {programmeAreas.map((area) => (
                          <div key={area} className="flex items-center space-x-2">
                            <Checkbox
                              id={`prog-${area}`}
                              checked={formData.programme_areas.includes(area)}
                              onCheckedChange={() => handleCheckboxToggle('programme_areas', area)}
                            />
                            <label htmlFor={`prog-${area}`} className="text-sm text-slate-600 cursor-pointer">
                              {area}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="preferred_roles">Preferred Role(s) (Optional)</Label>
                      <Input
                        id="preferred_roles"
                        name="preferred_roles"
                        value={formData.preferred_roles}
                        onChange={handleChange}
                        placeholder="e.g., Field volunteer, Trainer, Research assistant, Social media volunteer"
                        className="mt-1"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Hours per Week *</Label>
                        <Select 
                          value={formData.hours_per_week}
                          onValueChange={(value) => handleSelectChange('hours_per_week', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select hours" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2-5">2–5 hours</SelectItem>
                            <SelectItem value="6-10">6–10 hours</SelectItem>
                            <SelectItem value="11-20">11–20 hours</SelectItem>
                            <SelectItem value="20+">20+ hours</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="start_date">Start Date *</Label>
                        <Input
                          id="start_date"
                          name="start_date"
                          type="date"
                          value={formData.start_date}
                          onChange={handleChange}
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label>Duration *</Label>
                        <Select 
                          value={formData.duration}
                          onValueChange={(value) => handleSelectChange('duration', value)}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-3-months">1–3 months</SelectItem>
                            <SelectItem value="3-6-months">3–6 months</SelectItem>
                            <SelectItem value="6-12-months">6–12 months</SelectItem>
                            <SelectItem value="flexible">Flexible</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="time_zone">Time Zone *</Label>
                        <Input
                          id="time_zone"
                          name="time_zone"
                          value={formData.time_zone}
                          onChange={handleChange}
                          required
                          placeholder="e.g., CET, GMT+1, EST"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Section C - Skills & Experience */}
                {currentSection === 3 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                      <Briefcase className="w-6 h-6 text-[#E5B75F]" />
                      <h3 className="text-xl font-bold text-slate-900">Section C — Skills & Experience</h3>
                    </div>

                    <div>
                      <Label htmlFor="current_occupation">Current Occupation/Studies *</Label>
                      <Input
                        id="current_occupation"
                        name="current_occupation"
                        value={formData.current_occupation}
                        onChange={handleChange}
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Relevant Skills * (Tick all that apply)</Label>
                      <div className="grid md:grid-cols-3 gap-3 mt-3">
                        {skillOptions.map((skill) => (
                          <div key={skill} className="flex items-center space-x-2">
                            <Checkbox
                              id={`skill-${skill}`}
                              checked={formData.relevant_skills.includes(skill)}
                              onCheckedChange={() => handleCheckboxToggle('relevant_skills', skill)}
                            />
                            <label htmlFor={`skill-${skill}`} className="text-sm text-slate-600 cursor-pointer">
                              {skill}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="other_skills">Other Skills (Please specify)</Label>
                      <Input
                        id="other_skills"
                        name="other_skills"
                        value={formData.other_skills}
                        onChange={handleChange}
                        placeholder="Any other relevant skills"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="previous_experience">Previous Volunteer/Work Experience (Optional)</Label>
                      <Textarea
                        id="previous_experience"
                        name="previous_experience"
                        value={formData.previous_experience}
                        onChange={handleChange}
                        placeholder="Describe any relevant experience..."
                        className="mt-1 min-h-[100px]"
                      />
                    </div>

                    <div>
                      <Label htmlFor="motivation_statement">Short Motivation Statement *</Label>
                      <p className="text-sm text-slate-500 mb-2">
                        "Why do you want to volunteer with OPAD, and what impact do you hope to contribute?"
                      </p>
                      <Textarea
                        id="motivation_statement"
                        name="motivation_statement"
                        value={formData.motivation_statement}
                        onChange={handleChange}
                        required
                        placeholder="Share your motivation..."
                        className="mt-1 min-h-[120px]"
                      />
                    </div>

                    <div>
                      <Label>CV/Resume Upload * (PDF/DOC/DOCX, max 5MB)</Label>
                      <div className="mt-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full border-dashed border-2 h-20 flex flex-col items-center justify-center gap-2"
                        >
                          <Upload className="w-6 h-6 text-slate-400" />
                          {cvFile ? (
                            <span className="text-[#4A7C59] font-medium">{cvFile.name}</span>
                          ) : (
                            <span className="text-slate-500">Click to upload CV/Resume</span>
                          )}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="portfolio_linkedin">Portfolio/LinkedIn (Optional)</Label>
                      <Input
                        id="portfolio_linkedin"
                        name="portfolio_linkedin"
                        value={formData.portfolio_linkedin}
                        onChange={handleChange}
                        placeholder="https://linkedin.com/in/yourprofile"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* Section D - Location & Travel */}
                {currentSection === 4 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                      <Plane className="w-6 h-6 text-[#7BC4E2]" />
                      <h3 className="text-xl font-bold text-slate-900">Section D — Location & Travel</h3>
                    </div>
                    <p className="text-sm text-slate-500 -mt-2 mb-4">
                      (For in-country/international volunteers)
                    </p>

                    <div>
                      <Label className="text-base font-semibold">Are you able to travel if needed? *</Label>
                      <RadioGroup 
                        value={formData.able_to_travel}
                        onValueChange={(value) => handleSelectChange('able_to_travel', value)}
                        className="mt-3 space-y-2"
                      >
                        {['Yes', 'No', 'Maybe'].map((option) => (
                          <div key={option} className="flex items-center space-x-3">
                            <RadioGroupItem value={option.toLowerCase()} id={`travel-${option}`} />
                            <Label htmlFor={`travel-${option}`} className="font-normal cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Do you have a valid passport? (Optional)</Label>
                      <RadioGroup 
                        value={formData.has_passport}
                        onValueChange={(value) => handleSelectChange('has_passport', value)}
                        className="mt-3 space-y-2"
                      >
                        {['Yes', 'No'].map((option) => (
                          <div key={option} className="flex items-center space-x-3">
                            <RadioGroupItem value={option.toLowerCase()} id={`passport-${option}`} />
                            <Label htmlFor={`passport-${option}`} className="font-normal cursor-pointer">
                              {option}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div>
                      <Label htmlFor="visa_restrictions">Any visa restrictions? (Optional)</Label>
                      <Input
                        id="visa_restrictions"
                        name="visa_restrictions"
                        value={formData.visa_restrictions}
                        onChange={handleChange}
                        placeholder="Describe any visa limitations..."
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="preferred_regions">Preferred Countries/Regions to Support (Optional)</Label>
                      <Textarea
                        id="preferred_regions"
                        name="preferred_regions"
                        value={formData.preferred_regions}
                        onChange={handleChange}
                        placeholder="e.g., West Africa, Southeast Asia, Latin America..."
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* Section E - Safeguarding & Compliance */}
                {currentSection === 5 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                      <Shield className="w-6 h-6 text-[#E08B7A]" />
                      <h3 className="text-xl font-bold text-slate-900">Section E — Safeguarding & Compliance</h3>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-amber-800">
                        <strong>Important:</strong> OPAD is committed to safeguarding all individuals involved in our programmes. 
                        Please answer the following questions honestly.
                      </p>
                    </div>

                    <div>
                      <Label className="text-base font-semibold">Have you ever been convicted of a criminal offence? *</Label>
                      <RadioGroup 
                        value={formData.criminal_conviction}
                        onValueChange={(value) => handleSelectChange('criminal_conviction', value)}
                        className="mt-3 space-y-2"
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="no" id="conviction-no" />
                          <Label htmlFor="conviction-no" className="font-normal cursor-pointer">No</Label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="yes" id="conviction-yes" />
                          <Label htmlFor="conviction-yes" className="font-normal cursor-pointer">Yes (please explain)</Label>
                        </div>
                      </RadioGroup>
                      
                      {formData.criminal_conviction === 'yes' && (
                        <Textarea
                          name="criminal_explanation"
                          value={formData.criminal_explanation}
                          onChange={handleChange}
                          placeholder="Please provide details..."
                          className="mt-3"
                        />
                      )}
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                      <Checkbox
                        id="code_of_conduct"
                        checked={formData.agrees_to_code_of_conduct}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agrees_to_code_of_conduct: checked }))}
                      />
                      <label htmlFor="code_of_conduct" className="text-sm text-slate-700 cursor-pointer">
                        <strong>I agree to follow OPAD's Code of Conduct & Safeguarding Policy *</strong>
                      </label>
                    </div>

                    <div className="space-y-4">
                      <Label className="text-base font-semibold">References * (Two required)</Label>
                      
                      <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                        <p className="font-medium text-slate-700">Reference 1</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="reference1_name">Name *</Label>
                            <Input
                              id="reference1_name"
                              name="reference1_name"
                              value={formData.reference1_name}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reference1_organization">Organization</Label>
                            <Input
                              id="reference1_organization"
                              name="reference1_organization"
                              value={formData.reference1_organization}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reference1_email">Email *</Label>
                            <Input
                              id="reference1_email"
                              name="reference1_email"
                              type="email"
                              value={formData.reference1_email}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reference1_phone">Phone</Label>
                            <Input
                              id="reference1_phone"
                              name="reference1_phone"
                              value={formData.reference1_phone}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                        <p className="font-medium text-slate-700">Reference 2</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="reference2_name">Name *</Label>
                            <Input
                              id="reference2_name"
                              name="reference2_name"
                              value={formData.reference2_name}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reference2_organization">Organization</Label>
                            <Input
                              id="reference2_organization"
                              name="reference2_organization"
                              value={formData.reference2_organization}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reference2_email">Email *</Label>
                            <Input
                              id="reference2_email"
                              name="reference2_email"
                              type="email"
                              value={formData.reference2_email}
                              onChange={handleChange}
                              required
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label htmlFor="reference2_phone">Phone</Label>
                            <Input
                              id="reference2_phone"
                              name="reference2_phone"
                              value={formData.reference2_phone}
                              onChange={handleChange}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Section F - Consent & Data Protection */}
                {currentSection === 6 && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                      <FileText className="w-6 h-6 text-[#4A7C59]" />
                      <h3 className="text-xl font-bold text-slate-900">Section F — Consent & Data Protection</h3>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <p className="text-sm text-blue-800">
                        <strong>GDPR Notice:</strong> Your personal data will be processed in accordance with OPAD's 
                        privacy policy and applicable data protection regulations.
                      </p>
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                      <Checkbox
                        id="consent_data"
                        checked={formData.consent_data_processing}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_data_processing: checked }))}
                      />
                      <label htmlFor="consent_data" className="text-sm text-slate-700 cursor-pointer">
                        <strong>I consent to OPAD collecting and processing my information for volunteer recruitment 
                        and placement. I can request deletion at any time. *</strong>
                      </label>
                    </div>

                    <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                      <Checkbox
                        id="consent_newsletter"
                        checked={formData.consent_newsletter}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, consent_newsletter: checked }))}
                      />
                      <label htmlFor="consent_newsletter" className="text-sm text-slate-700 cursor-pointer">
                        I would like to receive OPAD news and volunteer opportunities by email. (Optional)
                      </label>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t">
                  {currentSection > 1 ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevSection}
                      className="px-6"
                    >
                      Previous
                    </Button>
                  ) : (
                    <div></div>
                  )}

                  {currentSection < 6 ? (
                    <Button
                      type="button"
                      onClick={nextSection}
                      className="bg-[#4A7C59] hover:bg-[#3A6B49] text-white px-8"
                    >
                      Next Section
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#E08B7A] hover:bg-[#D07A69] text-white px-8"
                      data-testid="volunteer-submit"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Apply to Volunteer
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Donate Section */}
      <section className="relative section-padding overflow-hidden" data-testid="donate-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E08B7A] via-[#E99A8A] to-[#F0A99A]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/10 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#E5B75F]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <Heart className="w-12 h-12 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
              Support Our Mission
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Your donation helps us continue our work in poverty alleviation, education, climate action, 
              and community development across the globe.
            </p>
            <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer" data-testid="donate-button">
              <Button 
                size="lg" 
                className="bg-white text-[#E08B7A] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold"
              >
                <Heart className="w-5 h-5 mr-2" />
                Donate Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
